import { useNavigate, useSearch } from '@tanstack/react-router'
import { useState, type ReactNode } from 'react'

import { cn } from '../../components/cn'
import { T } from '../../copy/text'
import { t } from '../../copy'
import {
  usePasswordLogin,
  useRequestEmailCode,
  useUpdatePassword,
  useVerifyEmailCode,
} from '../../app/queries'
import { ApiFailure } from '../../app/errors'

/**
 * 右边那半张表单。2026-08-25 认证简化后的形态：
 *
 * 两种登录方式——验证码登录、密码登录——顶部清晰切换，不再堆在一张表单里。
 * Passkey、恢复码、bootstrap token 全部退出用户界面（后端路由同步摘除，
 * 未挂载即 404；历史数据表不做破坏性删除）。
 *
 * 注册：邮箱 → 六位验证码 → 设置密码 → 自动登录。空平台引导走运维通道
 * （bootstrap 文件 + curl），不出现在普通注册页。
 *
 * 收码那一步：同一个输入框位置换成收码框，**满 6 位自动提交**——
 * 不多编一个按钮文案，也不让人多点一下。
 *
 * ## 失败必须看得见
 *
 * 这张表原来**一处都不显示错误**。用户点一下「发验证码」，页面纹丝不动，
 * 于是他会再点一次、再点一次。**一个静默的失败比一句难听的错误糟得多。**
 */

type Mode = 'code' | 'password'

export function AuthForm({
  signUp,
  onToggle,
  initialEmail = '',
}: {
  signUp: boolean
  onToggle: () => void
  /** 邀请链接带来的邮箱：预填，并直接走验证码那条路（他还没有密码）。 */
  initialEmail?: string
}) {
  const navigate = useNavigate()
  // return_to：被人从别的页请来登录时，登录完要回去的那条站内路径。
  // 现在只有邀请兑换页用它（/team/invite?token=… 没登录先来这儿）。
  const search = useSearch({ strict: false }) as { return_to?: string }
  const [mode, setMode] = useState<Mode>(initialEmail === '' ? 'password' : 'code')
  const [email, setEmail] = useState(initialEmail)
  const [password, setPassword] = useState('')
  const [code, setCode] = useState('')
  const [codeSent, setCodeSent] = useState(false)
  const [emailVerified, setEmailVerified] = useState(false)

  const request = useRequestEmailCode()
  const verify = useVerifyEmailCode()
  const passwordLogin = usePasswordLogin()
  const updatePassword = useUpdatePassword()

  /** 本地校验的那一句。**不发请求就能知道的事，不要靠请求去发现。** */
  const [localError, setLocalError] = useState<string | null>(null)

  const remoteFailure =
    request.error ?? verify.error ?? passwordLogin.error ?? updatePassword.error

  /**
   * 屏上那一句。本地校验优先；服务端的 401 在登录页不能说「登录已过期」
   * （站在这儿的人从来没进去过），换成具体哪条路的失败句。
   */
  const failure =
    localError ??
    (passwordLogin.error instanceof ApiFailure && passwordLogin.error.kind === 'unauthorized'
      ? t('auth.password.invalid')
      : remoteFailure instanceof Error
        ? remoteFailure.message
        : null)

  const minutes = Math.round((request.data?.expires_in_seconds ?? 600) / 60)
  const primaryPending =
    request.isPending || verify.isPending || updatePassword.isPending || passwordLogin.isPending
  const primaryDisabled = codeSent
    ? emailVerified
      ? password.length === 0 || primaryPending
      : code.length !== 6 || primaryPending
    : email.trim().length === 0 ||
      primaryPending ||
      (mode === 'password' && password.length === 0)

  /**
   * 登录完去哪。有 return_to 就回去（邀请兑换那条链：没登录点开邀请链接 →
   * 来这儿 → 登录完**带着 token 回到兑换页**，自动入队接上），没有就落 /lulu。
   *
   * 只认「以单个 / 开头的站内路径」：`//evil.example`（协议相对）和带协议的
   * 绝对地址都退回 /lulu——登录页不能变成开放重定向的跳板。
   */
  function navigateAfterSignIn() {
    const raw = search.return_to ?? ''
    const safe = raw.startsWith('/') && !raw.startsWith('//') ? raw : '/lulu'
    const [pathname, query = ''] = safe.split('?')
    const params = Object.fromEntries(new URLSearchParams(query))
    return navigate({ to: pathname as never, search: params as never })
  }

  async function submitCode(value: string) {
    setCode(value)
    if ((!emailVerified && value.length !== 6) || verify.isPending || updatePassword.isPending) return
    setLocalError(null)
    try {
      if (!emailVerified) {
        const signedIn = await verify.mutateAsync({ email: email.trim(), code: value })
        const alreadyHasPassword = signedIn.sign_in_paths.paths.some(
          (path) => path.kind === 'password' && path.available,
        )
        if (alreadyHasPassword) {
          await navigateAfterSignIn()
          return
        }
        setEmailVerified(true)
      }
      // Verify first: its Set-Cookie must be accepted before the authenticated password write.
      await updatePassword.mutateAsync({ new_password: password })
      await navigateAfterSignIn()
    } catch {
      // TanStack Mutation owns the visible error. Staying here is the important part.
    }
  }

  async function submitPassword() {
    setLocalError(null)
    try {
      await passwordLogin.mutateAsync({ email: email.trim(), password })
      await navigateAfterSignIn()
    } catch {
      // The mutation error is rendered below with role=alert.
    }
  }

  async function submitPrimary() {
    setLocalError(null)
    if (mode === 'password' && new TextEncoder().encode(password).length > 72) {
      setLocalError(t('auth.password.weak'))
      return
    }
    if (codeSent) {
      await submitCode(code)
      return
    }
    if (mode === 'code' || signUp) {
      try {
        await request.mutateAsync(email.trim())
        setCodeSent(true)
      } catch {
        // The mutation error is rendered below with role=alert.
      }
      return
    }
    await submitPassword()
  }

  function resetFailures() {
    setLocalError(null)
    request.reset()
    verify.reset()
    passwordLogin.reset()
    updatePassword.reset()
  }

  function switchMode() {
    resetFailures()
    setCode('')
    setCodeSent(false)
    setEmailVerified(false)
    onToggle()
  }

  function switchTo(next: Mode) {
    if (mode === next) return
    resetFailures()
    setCode('')
    setCodeSent(false)
    setEmailVerified(false)
    setMode(next)
  }

  return (
    <div className="auth-theme w-full max-w-[400px]">
      <h1 className="m-0 mb-[7px] font-npc-display text-npc-h1-xs font-semibold">
        <T id={signUp ? 'auth.signup.title' : 'auth.signin.title'} />
      </h1>
      <p className="m-0 mb-npc-modal text-npc-section-sm font-normal leading-[1.6] text-npc-ink-soft">
        {emailVerified ? (
          <T id="auth.password.hint" />
        ) : codeSent ? (
          <T id="auth.code.sent" vars={{ email, minutes }} />
        ) : (
          <T id={signUp ? 'auth.signup.subtitle' : 'auth.signin.subtitle'} />
        )}
      </p>

      {/* 模式切换：验证码 / 密码。两种方式清晰的分段控制，不堆一张表单 */}
      {!signUp && !codeSent ? (
        <div
          role="tablist"
          aria-label={t('auth.signin.title')}
          className="mb-npc-card flex rounded-npc-inset border border-npc-border bg-npc-surface p-[3px]"
        >
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'code'}
            onClick={() => switchTo('code')}
            data-testid="auth-mode-code"
            className={cn(
              'flex-1 cursor-pointer rounded-npc-button border-none py-npc-gap-sm font-[inherit] text-npc-body-sm',
              mode === 'code'
                ? 'auth-seg-active'
                : 'bg-transparent text-npc-ink-soft',
            )}
          >
            <T id="auth.email_code.cta" />
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'password'}
            onClick={() => switchTo('password')}
            data-testid="auth-mode-password"
            className={cn(
              'flex-1 cursor-pointer rounded-npc-button border-none py-npc-gap-sm font-[inherit] text-npc-body-sm',
              mode === 'password'
                ? 'auth-seg-active'
                : 'bg-transparent text-npc-ink-soft',
            )}
          >
            <T id="auth.password.cta" />
          </button>
        </div>
      ) : null}

      <form
        onSubmit={(event) => {
          event.preventDefault()
          void submitPrimary()
        }}
      >
        <div className="mb-npc-card flex flex-col gap-npc-grid-sm">
          {codeSent && !emailVerified ? (
            <Field label={<T id="auth.field.code.label" />}>
              <input
                value={code}
                onChange={(event) =>
                  void submitCode(event.target.value.replace(/\D/g, '').slice(0, 6))
                }
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                required
                className={inputClass}
              />
            </Field>
          ) : emailVerified ? (
            <Field label={<T id="auth.field.new_password.label" />}>
              <input
                type="password"
                value={password}
                onChange={(event) => {
                  updatePassword.reset()
                  setLocalError(null)
                  setPassword(event.target.value)
                }}
                autoComplete="new-password"
                minLength={12}
                required
                className={inputClass}
              />
            </Field>
          ) : (
            <>
              <Field label={<T id="auth.field.email.label" />}>
                <input
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder={t('auth.field.email.placeholder')}
                  autoComplete="email"
                  required
                  className={inputClass}
                />
              </Field>
              {signUp || mode === 'password' ? (
                <Field label={<T id="auth.field.password.label" />}>
                  <input
                    type="password"
                    value={password}
                    onChange={(event) => {
                      updatePassword.reset()
                      setLocalError(null)
                      setPassword(event.target.value)
                    }}
                    placeholder={signUp ? t('auth.field.password.placeholder') : undefined}
                    autoComplete={signUp ? 'new-password' : 'current-password'}
                    minLength={signUp ? 12 : undefined}
                    required
                    className={inputClass}
                  />
                </Field>
              ) : null}
              {signUp ? (
                <p className="m-0 text-npc-caption text-npc-ink-soft">
                  <T id="auth.password.hint" />
                </p>
              ) : null}
            </>
          )}
        </div>

        {failure ? (
          <p
            role="alert"
            className="mb-npc-grid-sm m-0 rounded-npc-card-sm border border-npc-amber-border bg-npc-amber-bg px-npc-card-sm py-npc-gap-lg text-npc-caption leading-[1.7] text-npc-amber-ink"
          >
            {failure}
          </p>
        ) : null}

        {!codeSent || emailVerified ? (
          <button
            type="submit"
            disabled={primaryDisabled}
            className={cn(
              'auth-primary-cta mb-npc-grid-sm w-full cursor-pointer border-none',
              'py-npc-gap-lg font-[inherit] text-npc-body-lg',
              'disabled:cursor-not-allowed disabled:opacity-50',
            )}
          >
            <T
              id={
                emailVerified ? 'auth.password.set.cta'
                  : signUp ? 'auth.signup.cta'
                    : mode === 'code' ? 'auth.email_code.cta'
                      : 'auth.signin.cta'
              }
            />
          </button>
        ) : null}
      </form>

      <div className="mt-npc-grid-lg text-center text-npc-body-sm text-npc-ink-soft">
        <T id={signUp ? 'auth.signup.switch_hint' : 'auth.signin.switch_hint'} />
        <button
          type="button"
          onClick={switchMode}
          className="auth-switch-cta ml-[5px] cursor-pointer border-none bg-transparent p-0 font-[inherit]"
        >
          <T id={signUp ? 'auth.signup.switch_cta' : 'auth.signin.switch_cta'} />
        </button>
      </div>
    </div>
  )
}

const inputClass =
  'block w-full rounded-npc-inset border border-npc-border bg-npc-panel px-[15px] py-npc-grid-sm ' +
  'font-[inherit] text-npc-section-sm font-normal text-npc-ink placeholder:text-npc-ink-softer'

function Field({ label, children }: { label: ReactNode; children: ReactNode }) {
  return (
    <label className="block">
      <span className="mb-[7px] block text-npc-caption text-npc-ink-soft">{label}</span>
      {children}
    </label>
  )
}
